/**
 * PanelRail Component
 *
 * Collapsed rail view of the side panel showing icon buttons for Code and AI tabs.
 * Shows a notification badge when there are unread AI messages.
 */

import { makeStyles, tokens, Button, Tooltip, Badge } from '@fluentui/react-components';
import {
  CodeRegular,
  PanelRightExpandRegular,
} from '@fluentui/react-icons';
import { CopilotLogoIcon } from '@/components/common/CopilotIcon';
import type { SidePanelTab } from './SidePanel';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    width: '48px',
    backgroundColor: tokens.colorNeutralBackground2,
    borderLeft: `1px solid ${tokens.colorNeutralStroke1}`,
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalXS}`,
    gap: tokens.spacingVerticalXS,
  },
  expandButton: {
    minWidth: '36px',
    width: '36px',
    height: '36px',
    padding: 0,
  },
  tabButton: {
    minWidth: '36px',
    width: '36px',
    height: '36px',
    padding: 0,
    position: 'relative',
  },
  divider: {
    width: '100%',
    height: '1px',
    backgroundColor: tokens.colorNeutralStroke1,
    margin: `${tokens.spacingVerticalXS} 0`,
  },
  badge: {
    position: 'absolute',
    top: '2px',
    right: '2px',
  },
});

export interface PanelRailProps {
  /** Handler to expand the panel to a specific tab */
  onExpand: (tab: SidePanelTab) => void;
  /** Whether the AI tab has unread messages */
  hasUnreadMessages?: boolean;
  /** Whether to hide the Code button (Assistant-only mode) */
  hideCode?: boolean;
}

export const PanelRail: React.FC<PanelRailProps> = ({
  onExpand,
  hasUnreadMessages = false,
  hideCode = false,
}) => {
  const styles = useStyles();

  return (
    <div className={styles.container}>
      <Tooltip content="Expand panel" relationship="label" positioning="before">
        <Button
          className={styles.expandButton}
          appearance="subtle"
          icon={<PanelRightExpandRegular />}
          onClick={() => onExpand(hideCode ? 'chat' : 'code')}
          aria-label="Expand panel"
        />
      </Tooltip>

      <div className={styles.divider} />

      {!hideCode && (
        <Tooltip content="Code" relationship="label" positioning="before">
          <Button
            className={styles.tabButton}
            appearance="subtle"
            icon={<CodeRegular />}
            onClick={() => onExpand('code')}
            aria-label="Open Code panel"
          />
        </Tooltip>
      )}

      <Tooltip content="Copilot" relationship="label" positioning="before">
        <Button
          className={styles.tabButton}
          appearance="subtle"
          icon={<CopilotLogoIcon />}
          onClick={() => onExpand('chat')}
          aria-label={
            hasUnreadMessages ? 'Open Copilot panel (unread messages)' : 'Open Copilot panel'
          }
        >
          {hasUnreadMessages && (
            <Badge
              className={styles.badge}
              size="tiny"
              color="danger"
              aria-hidden="true"
            />
          )}
        </Button>
      </Tooltip>
    </div>
  );
};

export default PanelRail;
